'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface ServiceTile {
  id: number;
  title: string;
  icon: string;
  description: string;
  dealershipPrice: string;
  independentPrice: string;
  savings: string;
  savingsPercent: number;
}

const FeaturedServices = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: ServiceTile[] = [
    {
      id: 1,
      title: 'Brake Service',
      icon: 'StopIcon',
      description: 'Complete brake pad and rotor replacement',
      dealershipPrice: '$800 - $1,200',
      independentPrice: '$450 - $700',
      savings: '$350',
      savingsPercent: 40
    },
    {
      id: 2,
      title: 'Suspension Work',
      icon: 'ArrowsUpDownIcon',
      description: 'Struts, shocks, and control arm replacement',
      dealershipPrice: '$1,500 - $2,500',
      independentPrice: '$900 - $1,500',
      savings: '$600',
      savingsPercent: 35
    },
    {
      id: 3,
      title: 'Battery Replacement',
      icon: 'BoltIcon',
      description: 'OEM quality battery installation',
      dealershipPrice: '$400 - $600',
      independentPrice: '$200 - $350',
      savings: '$200',
      savingsPercent: 45
    },
    {
      id: 4,
      title: 'Oil Change',
      icon: 'BeakerIcon',
      description: 'Full synthetic oil and filter service',
      dealershipPrice: '$150 - $200',
      independentPrice: '$80 - $120',
      savings: '$70',
      savingsPercent: 40
    },
    {
      id: 5,
      title: 'Engine Diagnostics',
      icon: 'ComputerDesktopIcon',
      description: 'Complete diagnostic scan and report',
      dealershipPrice: '$200 - $300',
      independentPrice: '$100 - $150',
      savings: '$100',
      savingsPercent: 45
    },
    {
      id: 6,
      title: 'Transmission Service',
      icon: 'Cog6ToothIcon',
      description: 'Fluid flush and filter replacement',
      dealershipPrice: '$500 - $800',
      independentPrice: '$300 - $500',
      savings: '$200',
      savingsPercent: 35
    }
  ];

  return (
    <section className="bg-background py-16 sm:py-24">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-4">
            Popular Services & Estimated Savings
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary max-w-2xl mx-auto">
            Compare typical dealership prices with independent shop rates for common BMW and Lexus repairs
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service) => (
            <Link
              key={service.id}
              href={`/search-results?service=${encodeURIComponent(service.title)}`}
              className="group bg-card rounded-xl p-6 shadow-elevation-sm transition-smooth hover:shadow-elevation-lg hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 group-hover:bg-primary transition-smooth">
                  <Icon 
                    name={service.icon as any} 
                    size={24} 
                    className="text-primary group-hover:text-primary-foreground transition-smooth" 
                  />
                </div>
                <div className="flex items-center space-x-1 px-3 py-1 rounded-full bg-success/10">
                  <Icon name="ArrowDownIcon" size={14} className="text-success" />
                  <span className="font-mono text-xs font-medium text-success">
                    {service.savingsPercent}%
                  </span>
                </div>
              </div>

              <h3 className="font-heading text-xl font-semibold text-text-primary mb-2 group-hover:text-primary transition-smooth">
                {service.title}
              </h3>
              <p className="font-body text-sm text-text-secondary mb-4 leading-relaxed">
                {service.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <span className="font-caption text-xs text-text-secondary">Dealership:</span>
                  <span className="font-mono text-sm font-medium text-text-primary line-through opacity-60">
                    {service.dealershipPrice}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-caption text-xs text-text-secondary">Independent:</span>
                  <span className="font-mono text-sm font-medium text-primary">
                    {service.independentPrice}
                  </span>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm font-medium text-text-primary">
                    Avg. Savings:
                  </span>
                  <span className="font-mono text-lg font-bold text-success">
                    {service.savings}
                  </span>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center space-x-2 text-primary group-hover:text-primary/80 transition-smooth">
                <span className="font-body text-sm font-medium">Find Mechanics</span>
                <Icon name="ArrowRightIcon" size={16} />
              </div>
            </Link>
          ))}
        </div>

        <div className="bg-accent/10 rounded-xl p-6 sm:p-8 border border-accent/20">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Icon name="ExclamationTriangleIcon" size={32} className="text-accent flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                Pricing Disclaimer
              </h3>
              <p className="font-body text-sm text-text-secondary leading-relaxed">
                Prices shown are estimates based on typical local rates and may vary by vehicle model, parts availability, and shop location. Always request a detailed quote before authorizing repairs. Actual savings depend on specific service requirements and parts selection.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedServices;